/**
 * @file SQUELETTE extensible : reliques (tirage a 20 Cores), familiers (equipement), poissons, expeditions d'Adventure (20 Cores + 10 XP / h).
 */
import { Decimal } from '@shared/bignum';
import type { BiomeId, WalletId } from '@shared/enums';
import { fail, ok, okVoid } from '@shared/types';
import type { Result } from '@shared/types';
import type { SimContext } from '../Context';

const RELIC_DRAW_COST = 20;
const XP_PER_ADVENTURE_LEVEL = 100;

/** Noyaux par theme : les themes de stage supplementaires (automne, cerisiers) partagent les noyaux des 3 regions d'origine. */
const CORE_BY_BIOME: Record<BiomeId, WalletId> = { forest: 'coreForest', desert: 'coreDesert', tundra: 'coreTundra', autumn: 'coreForest', blossom: 'coreTundra' };
const coreOf = (biome: BiomeId): WalletId => CORE_BY_BIOME[biome];

export class CollectiblesService {
  constructor(private readonly ctx: SimContext) {}

  /** Tire une relique du biome (cout : 20 Cores du biome) et monte son niveau (plafond maxLevel). */
  drawRelic(biome: BiomeId): Result<{ relicId: string }> {
    const ctx = this.ctx;
    if (!ctx.services.unlocks.isFeatureUnlocked('relics')) return fail('locked');
    const pool = ctx.config.relics.filter((r) => r.biome === biome);
    if (pool.length === 0) return fail('unknownId');
    return ctx.tx.purchase({ [coreOf(biome)]: new Decimal(RELIC_DRAW_COST) }, `relic:${biome}`, () => {
      const relic = ctx.rng.misc.pick(pool);
      const levels = ctx.data.relics.levels;
      levels[relic.id] = Math.min(relic.maxLevel, (levels[relic.id] ?? 0) + 1);
      ctx.bus.emit('ModifiersDirty', { source: `relic:${relic.id}` });
      return ok({ relicId: relic.id });
    });
  }

  /** Achat d'un familier en Pieces de familier ; equipe d'office s'il reste un emplacement libre. */
  buyPet(petId: string): Result {
    const ctx = this.ctx;
    const def = ctx.config.pets.find((p) => p.id === petId);
    if (!def) return fail('unknownId');
    if (ctx.data.pets.owned[petId]) return fail('alreadyOwned');
    if (!ctx.services.unlocks.isFeatureUnlocked('pets')) return fail('locked');
    return ctx.tx.purchase(def.cost, `pet:${petId}`, () => {
      ctx.data.pets.owned[petId] = { level: 1, count: 1 };
      if (ctx.data.pets.equipped.length < ctx.config.balance.pets.maxEquipped) ctx.data.pets.equipped.push(petId);
      ctx.bus.emit('PetBought', { id: petId });
      ctx.bus.emit('ModifiersDirty', { source: `pet:${petId}` });
      return okVoid;
    });
  }

  /** Retire un familier equipe (son bonus cesse). */
  unequipPet(petId: string): Result {
    const ctx = this.ctx;
    if (!ctx.data.pets.equipped.includes(petId)) return fail('notOwned');
    ctx.data.pets.equipped = ctx.data.pets.equipped.filter((p) => p !== petId);
    ctx.bus.emit('ModifiersDirty', { source: `pet:${petId}` });
    return okVoid;
  }

  /** Equipe un familier possede dans un des emplacements (BALANCE.pets.maxEquipped). */
  equipPet(petId: string, slot: number): Result {
    const ctx = this.ctx;
    const max = ctx.config.balance.pets.maxEquipped;
    if (!ctx.data.pets.owned[petId]) return fail('notOwned');
    if (slot < 0 || slot >= max) return fail('invalidArgument');
    const eq = ctx.data.pets.equipped.filter((p) => p !== petId);
    eq.splice(Math.min(slot, eq.length), 0, petId);
    ctx.data.pets.equipped = eq.slice(0, max);
    ctx.bus.emit('ModifiersDirty', { source: `pet:${petId}` });
    return okVoid;
  }

  /** Demarre une expedition (1 ticket 'adventure') sur la carte `biome` ; une seule par biome. */
  startExpedition(biome: BiomeId, petId: string | null): Result {
    const ctx = this.ctx;
    const adv = ctx.data.adventure;
    if (!ctx.services.unlocks.isUnlocked(ctx.config.modes.adventure.entry.unlock)) return fail('locked');
    if (adv.expeditions.some((e) => e.biome === biome)) return fail('cooldown');
    if (petId !== null && !ctx.data.pets.owned[petId]) return fail('notOwned');
    const consumed = ctx.services.tickets.consume('adventure');
    if (!consumed.ok) return consumed;
    adv.expeditions.push({ biome, petId, startedMs: ctx.rt.wallMs, durationSeconds: ctx.config.modes.adventure.params.expeditionHours * 3600 });
    return okVoid;
  }

  /** Expeditions terminees : Cores du biome + XP d'aventure, puis retrait. */
  claimExpeditions(): Result<{ claimed: number }> {
    const ctx = this.ctx;
    const adv = ctx.data.adventure;
    const cfg = ctx.config.balance.adventure;
    const done = adv.expeditions.filter((e) => ctx.rt.wallMs >= e.startedMs + e.durationSeconds * 1000);
    if (done.length === 0) return fail('nothingToClaim');
    for (const e of done) {
      const hours = e.durationSeconds / 3600;
      ctx.wallet.grant({ [coreOf(e.biome)]: new Decimal(Math.round(cfg.coresPerHour * hours)) }, 'expedition');
      adv.xp += Math.round(cfg.xpPerHour * hours * ctx.stats.getNumber('xpGain'));
    }
    adv.level = Math.min(cfg.maxLevel, 1 + Math.floor(adv.xp / XP_PER_ADVENTURE_LEVEL));
    adv.expeditions = adv.expeditions.filter((e) => !done.includes(e));
    return ok({ claimed: done.length });
  }
}
